import {
  MAX_MOD_DEPTH,
  WEAPON_EQUIP_SLOTS,
  aggregateWeaponStats,
  bodySlotsGrid,
  findNestedItem,
  findFreePlacement,
  getItemDef,
  GRID_AREAS,
  isTrueMagazine,
  opticZoom,
  recoilMultiplier,
  weaponComplete,
  weaponDurability,
  type AreaId,
  type AttachKind,
  type AttachmentItemDef,
  type Inventory,
  type ItemInstance,
  type TargetRef,
  type WeaponItemDef,
  type WeaponSlotDef,
} from "@rietz/shared";
import type { OpenContainer } from "../store.js";
import { gridOfArea } from "./drag.js";

/**
 * The Werkbank's decisions, with no React or DOM: what the tree looks like
 * (every host's slots in declaration order, each fitted host's block indented
 * beneath, every row carrying its `hostIid`), where a detached part goes
 * (first free placement over the player's OWN grids, never the crate), what
 * may be fitted (the slot's `accepts` over reachable grids), and what the
 * numbers do. No rules are reimplemented — `aggregateWeaponStats`,
 * `recoilMultiplier`, `opticZoom`, `weaponComplete` and `findFreePlacement`
 * are the shared functions, so the table cannot drift from the gun fired.
 */

// ---------------------------------------------------------------------------
// Finding the weapon
// ---------------------------------------------------------------------------

/** A weapon the player owns, and where it is sitting. */
export interface OwnedWeapon {
  item: ItemInstance;
  def: WeaponItemDef;
  /** `null` when it is in a grid rather than in the weapon slot; `"body"` when
   *  it is slung on the corpse being looted (protocol v21). */
  area: AreaId | "body" | null;
}

/**
 * The weapon `iid` names, if reachable — equipped, in a grid, in the open
 * container, in a nested box, or worn by the body being looted (the ops layer
 * finds it through the body pseudo-grid, which is how attachments are looted
 * without taking the gun). `area` is the top-level root.
 */
export function findOwnedWeapon(
  inv: Inventory,
  iid: string,
  container: OpenContainer | null = null,
): OwnedWeapon | null {
  for (const slot of WEAPON_EQUIP_SLOTS) {
    const equipped = inv.equipment[slot];
    if (equipped !== null && equipped.iid === iid) {
      const def = weaponDefOf(equipped);
      return def === null ? null : { item: equipped, def, area: null };
    }
  }
  for (const area of [...GRID_AREAS, "container"] as const) {
    const grid = gridOfArea(inv, container, area);
    if (grid === null) continue;
    const hit = findNestedItem([grid], iid);
    if (hit === null) continue;
    const def = weaponDefOf(hit.item);
    return def === null ? null : { item: hit.item, def, area };
  }
  const equipment = container?.equipment;
  if (equipment !== undefined) {
    const hit = findNestedItem([bodySlotsGrid(equipment)], iid);
    if (hit !== null) {
      const def = weaponDefOf(hit.item);
      return def === null ? null : { item: hit.item, def, area: "body" };
    }
  }
  return null;
}

function weaponDefOf(item: ItemInstance): WeaponItemDef | null {
  const def = getItemDef(item.defId);
  return def !== null && def.kind === "weapon" ? def : null;
}

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------

/**
 * One row of the bench's part tree: a mounting point, what is in it, and WHOSE
 * it is (`hostIid`) — the gun at depth 0, the fitted part deeper.
 */
export interface ModSlotRow {
  slot: WeaponSlotDef;
  /**
   * The host this slot belongs to; goes on the op as `hostIid`. Always sent,
   * even for the gun's own rows — one shape.
   */
  hostIid: string;
  /** The host's def, so a row's `accepts` whitelist and its label both come from
   *  the thing that actually declares the slot. */
  hostDef: WeaponItemDef | AttachmentItemDef;
  /** 0 for a slot on the gun, 1 for a slot on a part fitted to it. One indent
   *  level each; `MAX_MOD_DEPTH` keeps the tree two rows deep at most. */
  depth: number;
  /** Unique across hosts, unlike `slot.id`: two hosts on one gun may both
   *  declare a `tactical` slot, and React would otherwise key them alike. */
  key: string;
  /** The fitted part, or `null` for an empty slot. */
  fitted: { item: ItemInstance; def: AttachmentItemDef } | null;
  /**
   * The gun does not fire while this slot is empty (`WeaponSlotDef.vital`). On
   * every row, so the pane can warn BEFORE the part comes off.
   */
  vital: boolean;
  /**
   * Where "Abnehmen" would put the part, or `null` (the row is disabled). Sized
   * on the part alone — the subtree rides on the instance.
   */
  detachTo: TargetRef | null;
  /**
   * Rounds over capacity for a fitted true magazine, both off the magazine;
   * `null` for every other row.
   */
  load: { rounds: number; capacity: number } | null;
}

/**
 * The whole rifle as rows, depth-first in fold order: declaration order at
 * every level, a fitted host's block immediately below its own row (the optic
 * on a railed cover is indented under the cover — take the cover off and the
 * glass goes with it). Two levels by construction (`MAX_MOD_DEPTH`).
 * Re-resolved from the live instance every render. A gun with no `slots`
 * yields an empty list.
 */
export function modSlotRows(
  def: WeaponItemDef,
  item: ItemInstance,
  inv: Inventory,
  ownedContainer: OpenContainer | null = null,
): ModSlotRow[] {
  const out: ModSlotRow[] = [];

  const walk = (
    hostDef: WeaponItemDef | AttachmentItemDef,
    hostItem: ItemInstance,
    depth: number,
  ): void => {
    for (const slot of hostDef.slots ?? []) {
      const fitted = fittedIn(slot, hostItem.mods?.[slot.id]);
      const mag = fitted !== null && isTrueMagazine(fitted.def) ? fitted.def : null;
      out.push({
        slot,
        hostIid: hostItem.iid,
        hostDef,
        depth,
        key: `${hostItem.iid}:${slot.id}`,
        fitted,
        vital: slot.vital === true,
        detachTo: fitted === null ? null : detachTargetFor(inv, fitted.def, ownedContainer),
        load:
          mag === null || fitted === null
            ? null
            : { rounds: fitted.item.mag ?? 0, capacity: mag.magCapacity },
      });
      // A part here sits at `depth + 1`; its children at `depth + 2`, past the
      // cap.
      if (fitted === null || depth + 2 > MAX_MOD_DEPTH) continue;
      walk(fitted.def, fitted.item, depth + 1);
    }
  };
  walk(def, item, 0);
  return out;
}

/**
 * The sentence across the top of a rifle in pieces, or `null` — the UI half of
 * `acceptFire`'s "incomplete", asked of the same `weaponComplete`.
 */
export function modIncompleteWarning(def: WeaponItemDef, item: ItemInstance): string | null {
  return weaponComplete(def, item) ? null : "Incomplete — this weapon will not fire.";
}

/**
 * The part in a slot, if the slot could legally hold it — `fittedMods`' three
 * tests, so the list and the stat table describe one gun.
 */
function fittedIn(
  slot: WeaponSlotDef,
  raw: ItemInstance | undefined,
): { item: ItemInstance; def: AttachmentItemDef } | null {
  if (raw === undefined) return null;
  const def = getItemDef(raw.defId);
  if (def === null || def.kind !== "attachment") return null;
  if (def.attachKind !== slot.id) return null;
  if (!slot.accepts.includes(def.id)) return null;
  return { item: raw, def };
}

/**
 * Where a detached part lands: the first free cell across the player's OWN
 * grids in canonical order, never the open container. `null` = nowhere; the
 * pane disables the row rather than firing an op that bounces with "bounds".
 */
export function detachTargetFor(
  inv: Inventory,
  def: AttachmentItemDef,
  ownedContainer: OpenContainer | null = null,
): TargetRef | null {
  for (const area of GRID_AREAS) {
    const grid = gridOfArea(inv, null, area);
    if (grid === null) continue;
    const spot = findFreePlacement(grid, def);
    if (spot !== null) return { area, x: spot.x, y: spot.y, rot: spot.rot };
  }
  if (ownedContainer !== null) {
    const spot = findFreePlacement(ownedContainer.grid, def);
    if (spot !== null) {
      return { area: "container", x: spot.x, y: spot.y, rot: spot.rot };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Compatible parts
// ---------------------------------------------------------------------------

/** One offer in the "passende Teile" list. */
export interface ModCandidate {
  item: ItemInstance;
  def: AttachmentItemDef;
  /** Which grid it is lying in — the label the player reads to find it again. */
  area: AreaId;
}

/**
 * Everything the player could screw into `slot` now. The open container IS
 * searched (the `attach` op lets the part come from a crate); equipment slots
 * are not. Ordered by area (own grids first, crate last) then reading order.
 * Per host for free: `slot` is whichever host's, and its `accepts` is the test.
 */
export function compatibleParts(
  inv: Inventory,
  container: OpenContainer | null,
  slot: WeaponSlotDef,
): ModCandidate[] {
  const out: ModCandidate[] = [];
  const areas: AreaId[] = [...GRID_AREAS, "container"];

  for (const area of areas) {
    const grid = gridOfArea(inv, container, area);
    if (grid === null) continue;
    const found: { p: { item: ItemInstance; x: number; y: number }; def: AttachmentItemDef }[] = [];
    for (const p of grid.items) {
      const def = getItemDef(p.item.defId);
      if (def === null || def.kind !== "attachment") continue;
      if (def.attachKind !== slot.id) continue;
      if (!slot.accepts.includes(def.id)) continue;
      found.push({ p, def });
    }
    found.sort((a, b) => (a.p.y === b.p.y ? a.p.x - b.p.x : a.p.y - b.p.y));
    for (const f of found) out.push({ item: f.p.item, def: f.def, area });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Live stats
// ---------------------------------------------------------------------------

/** Which direction is an improvement. Not obvious for half the rows. */
export type StatDirection = "higher" | "lower";

/** One line of the base → current table. */
export interface ModStatRow {
  key: string;
  label: string;
  base: number;
  current: number;
  baseText: string;
  currentText: string;
  /** Which way is up on this row, so a caller comparing two BUILDS (rather than
   *  base against current) can score its own pair — see the workbench. */
  dir: StatDirection;
  /** `0` when nothing changed, `1` when the current value is better, `-1` worse. */
  verdict: -1 | 0 | 1;
}

/**
 * Relative change below which two values are equal — authored multipliers do
 * not land on authored numbers (0.85 × 1.3 ≠ 1.105).
 */
const EPS = 1e-6;

/**
 * The stat table: the bare def against the instance. The base column is
 * `aggregateWeaponStats(def, null)`, not `def.stats`, so the columns cannot
 * diverge. Recoil is the client-only `def.recoil × recoilMultiplier` as a
 * percentage of the CARBINE. Every number comes from a shared accessor that
 * walks `fittedModsDeep` itself.
 */
export function modStatRows(def: WeaponItemDef, item: ItemInstance): ModStatRow[] {
  const base = aggregateWeaponStats(def, null);
  const now = aggregateWeaponStats(def, item);

  const rows: ModStatRow[] = [];
  const push = (
    key: string,
    label: string,
    b: number,
    c: number,
    dir: StatDirection,
    text: (v: number) => string,
  ): void => {
    rows.push({
      key,
      label,
      base: b,
      current: c,
      baseText: text(b),
      currentText: text(c),
      dir,
      verdict: verdictFor(b, c, dir),
    });
  };

  push("rpm", "Rate of fire", base.rpm, now.rpm, "higher", (v) => `${Math.round(v)}/min`);
  // The magazine row's base is zero on a mag-fed gun — the receiver holds
  // nothing, and printing the factory capacity would credit a box the player
  // may not own.
  push("mag", "Magazine", base.magSize, now.magSize, "higher", (v) => `${Math.round(v)}`);
  push("reload", "Reload", base.reloadSec, now.reloadSec, "lower", (v) => secs(v));
  // Ergonomics and ADS time are one stat read twice (`adsTimeForErgo`); both
  // shown — points are what parts are priced in, seconds what the player feels.
  push("ergo", "Ergonomics", base.ergonomics, now.ergonomics, "higher", (v) => `${Math.round(v)}`);
  push("ads", "ADS time", base.adsTimeSec, now.adsTimeSec, "lower", (v) => secs(v));
  push("spread", "Spread", base.hipSpreadDeg, now.hipSpreadDeg, "lower", (v) => deg(v));
  push(
    "v0",
    "V₀",
    base.muzzleVelocity,
    now.muzzleVelocity,
    "higher",
    (v) => `${Math.round(v)} m/s`,
  );
  // `opticZoom` is `null` for iron sights, and 1× is the honest reading of that:
  // the naked eye magnifies by one.
  push(
    "zoom",
    "Magnification",
    1,
    opticZoom(def, item) ?? 1,
    "higher",
    (v) => `${num(v, 0)}×`,
  );
  push(
    "recoil",
    "Recoil",
    def.recoil,
    def.recoil * recoilMultiplier(def, item),
    "lower",
    (v) => `${Math.round(v * 100)} %`,
  );
  // Condition — the one row no attachment moves, here because the Werkbank IS
  // the inspect screen. The base is the def's hundred, not the repaired-down
  // ceiling, so a rebuilt gun shows what the repairs cost. A never-fired gun
  // reads as its ceiling and scores no verdict.
  push(
    "condition",
    "Condition",
    def.maxDurability,
    weaponDurability(def, item),
    "higher",
    (v) => `${num((v / def.maxDurability) * 100, 0)} %`,
  );
  return rows;
}

/** Better, worse or the same — `EPS` apart counts as the same. Exported so a
 *  screen comparing two builds scores them exactly as this table does. */
export function verdictFor(base: number, current: number, dir: StatDirection): -1 | 0 | 1 {
  const scale = Math.max(Math.abs(base), 1e-9);
  if (Math.abs(current - base) / scale < EPS) return 0;
  const up = current > base;
  return (dir === "higher") === up ? 1 : -1;
}

/** English decimal point, trailing zeros trimmed to `digits`. */
function num(v: number, digits: number): string {
  return v.toFixed(digits);
}

function secs(v: number): string {
  return `${num(v, 2)} s`;
}

function deg(v: number): string {
  return `${num(v, 2)}°`;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** Slot names as the Werkbank prints them. Exhaustive over `AttachKind`, so
 *  adding a kind to the union is a compile error here until it has a name. */
export const SLOT_LABELS: Record<AttachKind, string> = {
  optic: "Optic",
  muzzle: "Muzzle",
  grip: "Grip",
  stock: "Stock",
  mag: "Magazine",
  barrel: "Barrel",
  handguard: "Handguard",
  pistolgrip: "Pistol grip",
  receiver: "Receiver",
  tactical: "Tactical",
};

/** Where a candidate part is lying, for the line under its name. */
export const AREA_LABELS: Record<AreaId, string> = {
  pockets: "Pockets",
  rig: "Tactical rig",
  backpack: "Backpack",
  container: "Container",
};
